import { MaxDeadlineUnit, QualityParameter, QualityTicket, TicketKind } from '../../types/quality'

/**
 * Categorias de SLA de 1ª resposta para um ticket.
 * - `no_sla`       : sem parâmetro de SLA configurado para o tipo de ticket.
 * - `met`          : 1ª resposta existe e está dentro do prazo.
 * - `breached`     : 1ª resposta existe mas foi após o prazo.
 * - `pending_ok`   : sem 1ª resposta ainda, mas dentro do prazo.
 * - `pending_breached`: sem 1ª resposta e prazo já vencido.
 */
export type SlaFirstResponseStatus =
  | 'no_sla'
  | 'met'
  | 'breached'
  | 'pending_ok'
  | 'pending_breached'

/**
 * Classifica o SLA de primeira resposta de um ticket.
 * Usa a mesma lógica do TicketDetailDrawer e da coluna "Tempo Abertura".
 */
export function classifySlaPrimeiraResposta(
  ticket: Pick<QualityTicket, 'created_at' | 'kind'>,
  parameters: QualityParameter[],
  firstCommentIso: string | undefined,
): SlaFirstResponseStatus {
  const slaHours = parseSlaHours(parameters, ticket.kind)
  if (slaHours == null) return 'no_sla'

  const { dayStartHour, dayEndHour } = parseQualityBusinessDayWindow(parameters)
  const start    = new Date(ticket.created_at)
  const deadline = addBusinessHours(start, slaHours, dayStartHour, dayEndHour)

  if (firstCommentIso) {
    const firstDate = new Date(firstCommentIso)
    return firstDate.getTime() <= deadline.getTime() ? 'met' : 'breached'
  }

  return Date.now() <= deadline.getTime() ? 'pending_ok' : 'pending_breached'
}

// Dias úteis para contagem de SLA: segunda (1) a sexta (5), fuso local do navegador.
// Sábado (6) e domingo (0) não contribuem para o total.

/**
 * Lê `hora_util_inicio` e `hora_util_fim` de `quality_parameters`.
 * Fallback: 8 e 18 (janela padrão 08:00–18:00).
 * Garante que dayStartHour < dayEndHour e que ambos estão em [0, 23].
 */
export function parseQualityBusinessDayWindow(parameters: QualityParameter[]): {
  dayStartHour: number
  dayEndHour: number
} {
  const clamp = (v: number) => Math.max(0, Math.min(23, v))

  const startParam = parameters.find(p => p.key === 'hora_util_inicio')
  const endParam   = parameters.find(p => p.key === 'hora_util_fim')

  let dayStartHour = startParam ? clamp(parseInt(startParam.value, 10)) : 8
  let dayEndHour   = endParam   ? clamp(parseInt(endParam.value, 10))   : 18

  if (isNaN(dayStartHour)) dayStartHour = 8
  if (isNaN(dayEndHour))   dayEndHour   = 18
  if (dayStartHour >= dayEndHour) { dayStartHour = 8; dayEndHour = 18 }

  return { dayStartHour, dayEndHour }
}

/**
 * Calcula quantos milissegundos de tempo útil existem entre `start` e `end`.
 *
 * Regras de SLA:
 *  - Apenas dias úteis (seg–sex, `getDay()` 1–5, fuso local).
 *  - Dentro de cada dia útil, conta somente o intervalo [dayStartHour, dayEndHour).
 *  - Sábado, domingo e horário fora da janela diária = contribuição zero.
 *
 * Algoritmo: itera dia a dia (em tempo de calendário local) calculando a
 * sobreposição entre o intervalo `[start, end]` e a janela útil de cada dia.
 */
export function getElapsedBusinessHoursMs(
  start: Date,
  end: Date,
  dayStartHour: number,
  dayEndHour: number,
): number {
  if (end <= start) return 0

  const msPerHour = 60 * 60 * 1000
  let total = 0

  // Cursor aponta para o início do dia atual (meia-noite local)
  const cursor = new Date(start)
  cursor.setHours(0, 0, 0, 0)

  while (cursor < end) {
    const dayOfWeek = cursor.getDay() // 0=Dom, 1=Seg, …, 6=Sáb

    if (dayOfWeek >= 1 && dayOfWeek <= 5) {
      // Janela útil deste dia no calendário local
      const windowStart = new Date(cursor)
      windowStart.setHours(dayStartHour, 0, 0, 0)

      const windowEnd = new Date(cursor)
      windowEnd.setHours(dayEndHour, 0, 0, 0)

      // Sobreposição entre [start, end] e [windowStart, windowEnd]
      const overlapStart = Math.max(start.getTime(), windowStart.getTime())
      const overlapEnd   = Math.min(end.getTime(),   windowEnd.getTime())

      if (overlapEnd > overlapStart) {
        total += overlapEnd - overlapStart
      }
    }

    // Avançar para o próximo dia
    cursor.setDate(cursor.getDate() + 1)
  }

  return total
}

/**
 * Formata um número de horas para exibição na célula da tabela.
 * Exemplos: 0,0h · 1,5h · 42,3h
 */
export function formatBusinessHours(ms: number): string {
  const hours = ms / (60 * 60 * 1000)
  return `${hours.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}h`
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** Valida `value` como UUID de parâmetro de qualidade (ex.: grupo em `user_groups`). */
export function parseQualityParameterUuidValue(raw: string | null | undefined): string | null {
  if (raw == null || !String(raw).trim()) return null
  const v = String(raw).trim()
  return UUID_RE.test(v) ? v : null
}

/** Lê um parâmetro da lista carregada e retorna o UUID se válido. */
export function parseQualityParameterUuid(parameters: QualityParameter[], key: string): string | null {
  const param = parameters.find(p => p.key === key)
  return parseQualityParameterUuidValue(param?.value)
}

/**
 * Extrai UUIDs válidos de um valor CSV (vírgulas). Tokens inválidos são ignorados.
 * Um único UUID sem vírgula continua válido (legado).
 */
export function parseSlaPrimeiraRespostaGroupIdsFromValue(raw: string | null | undefined): string[] {
  if (raw == null || !String(raw).trim()) return []
  const seen = new Set<string>()
  const out: string[] = []
  for (const token of String(raw).split(',')) {
    const v = token.trim()
    if (!UUID_RE.test(v)) continue
    const key = v.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    out.push(v)
  }
  return out
}

/**
 * Lê `sla_grupo_usuario_primeira_resposta` de `quality_parameters`.
 * Retorna os UUIDs de `user_groups` configurados. Lista vazia = legado
 * (primeiro comentário de qualquer autor encerra o SLA).
 */
export function parseSlaPrimeiraRespostaGroupIds(parameters: QualityParameter[]): string[] {
  const param = parameters.find(p => p.key === 'sla_grupo_usuario_primeira_resposta')
  return parseSlaPrimeiraRespostaGroupIdsFromValue(param?.value)
}

/**
 * Lê o SLA (em horas) de `quality_parameters` conforme o `kind` do ticket.
 * - incident    → `sla_erro_primeira_resposta`
 * - improvement / suggestion → `sla_melhoria_primeira_resposta`
 * Retorna `null` se a chave não existir ou o valor for inválido.
 */
export function parseSlaHours(parameters: QualityParameter[], kind: TicketKind): number | null {
  if (kind === 'not_applicable') return null
  const key = kind === 'incident' ? 'sla_erro_primeira_resposta' : 'sla_melhoria_primeira_resposta'
  const param = parameters.find(p => p.key === key)
  if (!param) return null
  const hours = parseFloat(param.value)
  return isNaN(hours) || hours <= 0 ? null : hours
}

/**
 * Soma `hoursToAdd` horas úteis a `start` e retorna a data/hora resultante.
 * Respeita a janela [dayStartHour, dayEndHour) e dias úteis seg–sex (fuso local).
 */
export function addBusinessHours(
  start: Date,
  hoursToAdd: number,
  dayStartHour: number,
  dayEndHour: number,
): Date {
  const msPerHour    = 60 * 60 * 1000
  const dayMs        = (dayEndHour - dayStartHour) * msPerHour
  let   remaining    = hoursToAdd * msPerHour

  // Cursor começa no instante de início, avançado para dentro da janela útil se necessário
  const cursor = new Date(start)

  // Se estiver fora da janela diária ou em fim de semana, avança para o próximo slot útil
  const advanceToNextSlot = () => {
    while (true) {
      const dow = cursor.getDay()
      if (dow === 0) { cursor.setDate(cursor.getDate() + 1); cursor.setHours(dayStartHour, 0, 0, 0); continue }
      if (dow === 6) { cursor.setDate(cursor.getDate() + 2); cursor.setHours(dayStartHour, 0, 0, 0); continue }
      // Dia útil — verificar se está dentro da janela
      const windowStart = new Date(cursor); windowStart.setHours(dayStartHour, 0, 0, 0)
      const windowEnd   = new Date(cursor); windowEnd.setHours(dayEndHour, 0, 0, 0)
      if (cursor < windowStart) { cursor.setHours(dayStartHour, 0, 0, 0); break }
      if (cursor >= windowEnd)  { cursor.setDate(cursor.getDate() + 1); cursor.setHours(dayStartHour, 0, 0, 0); continue }
      break
    }
  }

  advanceToNextSlot()

  while (remaining > 0) {
    const windowEnd = new Date(cursor)
    windowEnd.setHours(dayEndHour, 0, 0, 0)

    const availableInDay = windowEnd.getTime() - cursor.getTime()
    if (remaining <= availableInDay) {
      cursor.setTime(cursor.getTime() + remaining)
      remaining = 0
    } else {
      remaining -= availableInDay
      // Avança para o início do próximo dia útil
      cursor.setDate(cursor.getDate() + 1)
      cursor.setHours(dayStartHour, 0, 0, 0)
      advanceToNextSlot()
    }
  }

  return cursor
}

export const MAX_DEADLINE_UNIT_LABELS: Record<MaxDeadlineUnit, string> = {
  days: 'Dias',
  hours: 'Horas',
}

/** Calcula a data/hora limite do prazo máximo corrido a partir da abertura do ticket. */
export function computeMaxDeadline(createdAt: string, unit: MaxDeadlineUnit, value: number): Date {
  const start = new Date(createdAt)
  const ms = unit === 'days'
    ? value * 24 * 60 * 60 * 1000
    : value * 60 * 60 * 1000
  return new Date(start.getTime() + ms)
}

/** Indica se o prazo máximo corrido foi ultrapassado (usa closed_at se fechado, senão agora). */
export function isMaxDeadlineBreached(deadline: Date, closedAt?: string | null): boolean {
  const end = closedAt ? new Date(closedAt) : new Date()
  return end.getTime() > deadline.getTime()
}

/** Formata a data/hora limite do prazo máximo corrido (dd/mm HH:mm). */
export function formatMaxDeadlineDatePtBr(date: Date): string {
  const d = String(date.getDate()).padStart(2, '0')
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const h = String(date.getHours()).padStart(2, '0')
  const min = String(date.getMinutes()).padStart(2, '0')
  return `${d}/${m} ${h}:${min}`
}

/** Formata unidade + valor para exibição (ex.: "5 dias", "1 hora"). */
export function formatMaxDeadlineLabel(unit: MaxDeadlineUnit, value: number): string {
  if (unit === 'days') return value === 1 ? '1 dia' : `${value} dias`
  return value === 1 ? '1 hora' : `${value} horas`
}

/**
 * Categorias de prazo máximo corrido para um ticket.
 * - `no_deadline`      : sem prazo configurado no cadastro.
 * - `met`              : encerrado dentro do prazo.
 * - `breached`         : encerrado após o prazo.
 * - `pending_ok`       : aberto, ainda dentro do prazo.
 * - `pending_breached` : aberto, prazo vencido.
 */
export type MaxDeadlineStatus =
  | 'no_deadline'
  | 'met'
  | 'breached'
  | 'pending_ok'
  | 'pending_breached'

/** Classifica o prazo máximo corrido de um ticket. */
export function classifyMaxDeadline(
  ticket: Pick<QualityTicket, 'created_at' | 'closed_at' | 'max_deadline_unit' | 'max_deadline_value'>,
): MaxDeadlineStatus {
  const unit = ticket.max_deadline_unit
  const value = ticket.max_deadline_value
  if (!unit || !value || value <= 0) return 'no_deadline'

  const deadline = computeMaxDeadline(ticket.created_at, unit, value)

  if (ticket.closed_at) {
    const closed = new Date(ticket.closed_at)
    return closed.getTime() <= deadline.getTime() ? 'met' : 'breached'
  }

  return new Date().getTime() <= deadline.getTime() ? 'pending_ok' : 'pending_breached'
}

/** Formata tempo corrido (ms) para exibição em KPIs (dias ou horas). */
export function formatElapsedCalendarTime(ms: number): string {
  const hours = ms / (60 * 60 * 1000)
  if (hours >= 24) {
    const days = hours / 24
    return `${days.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} dias`
  }
  return `${hours.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}h`
}
